/**
 * Snapshot → handler-registry dispatcher.
 *
 * Feeds a `/api/v2/alerts/snapshot/` response through the same
 * centralized SMS handler registry that processes live WebSocket frames,
 * so decode logic is NOT forked. Each snapshot section is mapped to the
 * equivalent live WS event type:
 *
 *   active_alerts[*]          → `AlertHistory` handler
 *   room_statuses[*]          → `AlertRoomHistory` handler
 *   reunification_event       → `reunification.event.activated` handler
 *   reunification_students    → `reunification.student.status_changed` handler
 *   reunification_guardians   → `reunification.guardian.checked_in` handler
 *   reunification_staff       → `reunification.staff.assigned` handler
 *
 * Reconciliation: list-level keys are invalidated up front so anything
 * completed/ended while offline drops out of view once the server's
 * refetch returns the new (shorter) list. This is why dispatch is NOT
 * additive — it is authoritative-pull rather than client-side merge.
 * Reunification that completed while offline therefore disappears
 * automatically on the next list refetch (ADR-106 §4 is preserved
 * because an active reunification with no originating alert still
 * appears in `reunification_event`).
 *
 * Dedupe: the set of `seenMessageIds` is tracked by the caller (router).
 * Snapshot-sourced dispatches never carry a `requires_ack` message id,
 * so they don't pollute the set; the dedupe only suppresses server
 * replay frames that arrive in the same connect window and would
 * re-invalidate keys already handled by the snapshot.
 */

import { activeAlertKeys, alertHistoryKeys } from '../../hooks/queries/useAlerts';
import { reunificationEventKeys } from '../../hooks/queries/useReunification';
import type { AlertSnapshot } from '../../services/snapshot.service';
import type { HandlerDeps, SocketHandlerMap } from './types';

type SnapshotLike = Record<string, unknown>;

function safeInvoke(
  handlers: SocketHandlerMap,
  type: string,
  data: SnapshotLike,
  raw: SnapshotLike,
): void {
  const handler = handlers[type];
  if (!handler) return;
  try {
    void handler(data, raw);
  } catch (err) {
    console.error(`[snapshotDispatcher] error in ${type} handler:`, err);
  }
}

export function dispatchSnapshot(
  snapshot: AlertSnapshot,
  handlers: SocketHandlerMap,
  deps: HandlerDeps,
): void {
  const { queryClient } = deps;

  // Reconcile pass: invalidate list-level keys so items present locally
  // but absent from the snapshot (completed / cancelled while offline)
  // drop out of view after the server's refetch. The per-item dispatches
  // below add detail-level invalidations the list refetch alone cannot
  // cover (e.g. an alert detail view open on a now-cancelled alert).
  queryClient.invalidateQueries({ queryKey: alertHistoryKeys.all });
  queryClient.invalidateQueries({ queryKey: activeAlertKeys.all });
  queryClient.invalidateQueries({ queryKey: reunificationEventKeys.all });

  // --- Alerts ----------------------------------------------------------
  for (const alert of snapshot.active_alerts) {
    const data = alert as SnapshotLike;
    safeInvoke(handlers, 'AlertHistory', data, { type: 'AlertHistory', data });
  }
  for (const room of snapshot.room_statuses) {
    const data = room as SnapshotLike;
    safeInvoke(handlers, 'AlertRoomHistory', data, {
      type: 'AlertRoomHistory',
      data,
    });
  }

  // --- Reunification ---------------------------------------------------
  // ADR-106 §4: `reunification_event` can be non-null with no active
  // alert. When the field is null there is no reunification to restore;
  // the up-front list invalidation is enough for completed-while-offline
  // to reconcile away.
  const reEvent = snapshot.reunification_event;
  if (reEvent) {
    const eventId = reEvent.id;
    const data = { ...(reEvent as SnapshotLike), event_id: eventId };
    const raw = { type: 'reunification.event.activated', event_id: eventId, data };
    safeInvoke(handlers, 'reunification.event.activated', data, raw);

    if (snapshot.reunification_students.length > 0) {
      safeInvoke(
        handlers,
        'reunification.student.status_changed',
        { event_id: eventId },
        { type: 'reunification.student.status_changed', event_id: eventId },
      );
    }
    if (snapshot.reunification_guardians.length > 0) {
      safeInvoke(
        handlers,
        'reunification.guardian.checked_in',
        { event_id: eventId },
        { type: 'reunification.guardian.checked_in', event_id: eventId },
      );
    }
    if (snapshot.reunification_staff.length > 0) {
      safeInvoke(
        handlers,
        'reunification.staff.assigned',
        { event_id: eventId },
        { type: 'reunification.staff.assigned', event_id: eventId },
      );
    }
  }
}
